import React from 'react';
import { NavLink } from 'react-router-dom';
import { haptic } from '../lib/haptics';

/**
 * Workspace tab definitions - 1:1 mirror of the iOS app's app/(tabs)
 * layout (see /tmp/ios/repo/frontend/app/(tabs)/_layout.tsx:160-172).
 *
 * iOS uses Ionicons:
 *   dashboard → grid / grid-outline
 *   advice    → construct / construct-outline   (label: "Build")
 *   designs   → layers / layers-outline         (label: "Design")
 *   toolbox   → apps / apps-outline             (label: "More")
 *
 * To match those icons pixel-for-pixel on the web we render inline
 * SVGs whose paths are taken directly from the Ionicons v7 package
 * (MIT licence). When the tab is active we render the FILLED variant;
 * when inactive we render the OUTLINE variant, same dual-state UX
 * the iOS app uses.
 *
 * Active tab tint: #1d8efe (matches iOS).
 * The centre FAB lives in <QuickAddFab/> and is rendered separately.
 */

// ─── Ionicon SVG paths (24×24 viewBox) ──────────────────────────────
const IonGridOutline = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} fill="none" stroke="currentColor" strokeWidth={32} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="48" y="48" width="176" height="176" rx="20" ry="20" />
    <rect x="288" y="48" width="176" height="176" rx="20" ry="20" />
    <rect x="48" y="288" width="176" height="176" rx="20" ry="20" />
    <rect x="288" y="288" width="176" height="176" rx="20" ry="20" />
  </svg>
);
const IonGridFilled = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} fill="currentColor" aria-hidden="true">
    <rect x="48" y="48" width="176" height="176" rx="20" ry="20" />
    <rect x="288" y="48" width="176" height="176" rx="20" ry="20" />
    <rect x="48" y="288" width="176" height="176" rx="20" ry="20" />
    <rect x="288" y="288" width="176" height="176" rx="20" ry="20" />
  </svg>
);

const IonConstructOutline = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} aria-hidden="true">
    <path d="M436.67 184.11a27.17 27.17 0 01-38.3 0l-22.48-22.49a27.15 27.15 0 010-38.29l50.89-50.89a.85.85 0 00-.26-1.38C393.68 57 351.09 64.15 324.05 91c-25.88 25.69-27.35 64.27-17.87 98a27 27 0 01-7.67 27.14l-173 160.76a40.76 40.76 0 1057.57 57.54l162.15-173.3a27 27 0 0126.77-7.7c33.46 8.94 71.49 7.26 97.07-17.94 27.49-27.08 33.42-74.94 20.1-102.33a.85.85 0 00-1.36-.22z" fill="none" stroke="currentColor" strokeLinecap="round" strokeMiterlimit="10" strokeWidth="32"/>
    <path d="M224 284c-17.48-17-25.49-24.91-31-30.29a18.24 18.24 0 01-3.33-21.35 20.76 20.76 0 013.5-4.62l15.68-15.29a18.66 18.66 0 015.63-3.87 18.11 18.11 0 0120 3.62c5.45 5.29 15.43 15 33.41 32.52M317.07 291.3c40.95 38.1 90.62 83.27 110 99.41a13.46 13.46 0 01.94 19.92L394.63 444a14 14 0 01-20.29-.76c-16.53-19.18-61.09-67.11-99.27-107" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32"/>
    <path d="M17.34 193.5l29.41-28.74a4.71 4.71 0 013.41-1.35 4.85 4.85 0 013.41 1.35h0a9.86 9.86 0 008.19 2.77c3.83-.42 7.92-1.6 10.57-4.12 6-5.8-.94-17.23 4.34-24.54a207 207 0 0119.78-22.6c6-5.88 29.84-28.32 69.9-44.45A107.31 107.31 0 01206.67 64c22.59 0 40 10 46.26 15.67a89.54 89.54 0 0110.28 11.64 78.92 78.92 0 00-9.21-2.77 68.82 68.82 0 00-20-1.26c-13.33 1.09-29.41 7.26-38 14-13.9 11-19.87 25.72-20.81 44.71-.68 14.12 2.72 22.1 36.1 55.49a6.6 6.6 0 01-.34 9.16l-18.22 18a6.88 6.88 0 01-9.54.09c-21.94-21.94-36.65-33.09-45-38.16s-15.07-6.5-18.3-6.85a30.85 30.85 0 00-18.27 3.87 11.39 11.39 0 00-2.64 2 14.14 14.14 0 00.42 20.08l1.71 1.6a4.63 4.63 0 010 6.64L71.73 246.6a4.71 4.71 0 01-3.41 1.4 4.86 4.86 0 01-3.41-1.35l-47.57-46.43a4.88 4.88 0 010-6.72z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32"/>
  </svg>
);
const IonConstructFilled = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} fill="currentColor" aria-hidden="true">
    <path d="M503.58 126.2a16.85 16.85 0 00-27.07-4.55l-51.15 51.15a11.15 11.15 0 01-15.66 0l-22.48-22.48a11.17 11.17 0 010-15.67l50.88-50.89a16.85 16.85 0 00-5.27-27.4c-39.71-17-89.08-7.45-120 23.29-26.81 26.61-34.83 68-22 113.7a11 11 0 01-3.16 11.1L114.77 365.1a56.76 56.76 0 1080.14 80.18L357 272.08a11 11 0 0110.9-3.17c45 12 86 4 112.43-22 15.2-15 25.81-36.17 29.89-59.71 3.83-22.2 1.41-44.44-6.64-61z"/>
    <path d="M437.33 378.41c-13.94-11.59-43.72-38.4-74.07-66.22l-66.07 70.61c28.24 30 53.8 57.85 65 70.88l.07.08A30 30 0 00383.72 464h1.1a30.11 30.11 0 0021-8.62l.07-.07 33.43-33.37a29.46 29.46 0 00-2-43.53zM118.54 214.55a20.48 20.48 0 00-3-10.76 2.76 2.76 0 012.62-4.22h.06c.84.09 5.33.74 11.7 4.61 4.73 2.87 18.23 12.08 41.73 35.54a34.23 34.23 0 007.22 22.12l66.23-61.55a33.73 33.73 0 00-21.6-9.2 2.65 2.65 0 01-.21-.26l-.65-.69-24.54-33.84a28.45 28.45 0 01-4-26.11 35.23 35.23 0 0111.78-16.35c5.69-4.41 18.53-9.72 29.44-10.62a52.92 52.92 0 0115.19.94 65.57 65.57 0 017.06 2.13 15.46 15.46 0 002.15.63 16 16 0 0016.38-25.06c-.26-.35-1.32-1.79-2.89-3.73a91.85 91.85 0 00-9.6-10.36c-8.15-7.36-29.27-19.77-57-19.77a123.13 123.13 0 00-46.3 9c-38.37 15.45-63.47 36.58-75.01 47.79l-.09.09A222.14 222.14 0 0063.7 129.5a27 27 0 00-4.7 11.77 7.33 7.33 0 01-7.71 6.17H50.2a20.65 20.65 0 00-14.59 5.9L6.16 182.05l-.32.32a20.89 20.89 0 00-.24 28.72c.19.2.37.39.57.58L53.67 258a21 21 0 0014.65 6 20.65 20.65 0 0014.59-5.9l29.46-28.79a20.51 20.51 0 006.17-14.76z"/>
  </svg>
);

const IonLayersOutline = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} fill="none" stroke="currentColor" strokeWidth={32} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M448 341.37L256 448 64 341.37" />
    <path d="M448 240L256 346.67 64 240" />
    <path d="M256 64L64 170.67 256 277.33l192-106.66L256 64z" />
  </svg>
);
const IonLayersFilled = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} fill="currentColor" aria-hidden="true">
    <path d="M256 277.33L73.7 174.36a8 8 0 010-13.94l174.18-98.36a16.12 16.12 0 0116.24 0L438.3 160.42a8 8 0 010 13.94zM458.3 247.66l-30.37-17.15a8 8 0 00-7.89 0L264.18 318.16a16.05 16.05 0 01-16.36 0L91.96 230.51a8 8 0 00-7.89 0L53.7 247.66a8 8 0 000 13.94l194.18 109.65a16.12 16.12 0 0016.24 0L458.3 261.6a8 8 0 000-13.94zM458.3 349.66l-30.37-17.15a8 8 0 00-7.89 0L264.18 420.16a16.05 16.05 0 01-16.36 0L91.96 332.51a8 8 0 00-7.89 0L53.7 349.66a8 8 0 000 13.94L247.88 473.25a16.12 16.12 0 0016.24 0L458.3 363.6a8 8 0 000-13.94z" />
  </svg>
);

const IonAppsOutline = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} fill="none" stroke="currentColor" strokeWidth={32} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="64" y="64" width="80" height="80" rx="40" />
    <rect x="216" y="64" width="80" height="80" rx="40" />
    <rect x="368" y="64" width="80" height="80" rx="40" />
    <rect x="64" y="216" width="80" height="80" rx="40" />
    <rect x="216" y="216" width="80" height="80" rx="40" />
    <rect x="368" y="216" width="80" height="80" rx="40" />
    <rect x="64" y="368" width="80" height="80" rx="40" />
    <rect x="216" y="368" width="80" height="80" rx="40" />
    <rect x="368" y="368" width="80" height="80" rx="40" />
  </svg>
);
const IonAppsFilled = (p) => (
  <svg viewBox="0 0 512 512" width={p.size || 22} height={p.size || 22} fill="currentColor" aria-hidden="true">
    <circle cx="104" cy="104" r="56" />
    <circle cx="256" cy="104" r="56" />
    <circle cx="408" cy="104" r="56" />
    <circle cx="104" cy="256" r="56" />
    <circle cx="256" cy="256" r="56" />
    <circle cx="408" cy="256" r="56" />
    <circle cx="104" cy="408" r="56" />
    <circle cx="256" cy="408" r="56" />
    <circle cx="408" cy="408" r="56" />
  </svg>
);

export const WORKSPACE_TABS = [
  { key: 'dashboard', label: 'Dashboard', to: '/workspace/dashboard', IconActive: IonGridFilled,      IconInactive: IonGridOutline },
  { key: 'advice',    label: 'Build',     to: '/workspace/advice',    IconActive: IonConstructFilled, IconInactive: IonConstructOutline },
  { key: 'designs',   label: 'Design',    to: '/workspace/designs',   IconActive: IonLayersFilled,    IconInactive: IonLayersOutline },
  { key: 'toolbox',   label: 'More',      to: '/workspace/toolbox',   IconActive: IonAppsFilled,      IconInactive: IonAppsOutline },
];

const NavItem = ({ to, label, IconActive, IconInactive, kind }) => (
  <NavLink
    to={to}
    end
    onClick={() => haptic('light')}
    className={({ isActive }) =>
      `${kind === 'sidebar' ? 'bp-sidebar-link' : 'bp-tabbar-link'} ${isActive ? 'is-active' : ''}`
    }
    data-testid={`${kind}-tab-${label.toLowerCase()}`}
  >
    {({ isActive }) => {
      const Icon = isActive ? IconActive : IconInactive;
      const size = kind === 'sidebar' ? 18 : 24;
      return (
        <>
          <Icon size={size} />
          <span>{label}</span>
        </>
      );
    }}
  </NavLink>
);

export const SidebarNav = () => (
  <nav className="bp-sidebar-nav" aria-label="Workspace">
    {WORKSPACE_TABS.map((t) => (
      <NavItem key={t.key} kind="sidebar" to={t.to} label={t.label} IconActive={t.IconActive} IconInactive={t.IconInactive} />
    ))}
  </nav>
);

/**
 * Bottom tab bar with a centre spacer for the floating action button.
 * Matches iOS: 2 tabs | FAB spacer | 2 tabs
 */
export const BottomTabBar = () => {
  const left = WORKSPACE_TABS.slice(0, 2);
  const right = WORKSPACE_TABS.slice(2);
  return (
    <nav className="bp-tabbar" aria-label="Workspace tabs" data-testid="workspace-tabbar">
      {left.map((t) => (
        <NavItem key={t.key} kind="tabbar" to={t.to} label={t.label} IconActive={t.IconActive} IconInactive={t.IconInactive} />
      ))}
      <div className="bp-tabbar-fab-spacer" aria-hidden="true" />
      {right.map((t) => (
        <NavItem key={t.key} kind="tabbar" to={t.to} label={t.label} IconActive={t.IconActive} IconInactive={t.IconInactive} />
      ))}
    </nav>
  );
};
